import { Injectable, inject } from '@angular/core';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AuthStore } from '../../auth.store';
import { OrgId, UserId } from '../shared/ids';
import {
  type CoverageRequest,
  CoverageRequestId,
  type CoverageRequestStatus,
} from './coverage-request';
import {
  type CoverageRequestListResult,
  CoverageRequestRepository,
  type CoverageRequestResolveResult,
  type CoverageRequestSubmitInput,
} from './coverage-request-repository';
import { type OrderLineItem, parseOrderLineItem, parsePricingSnapshot } from './order-line-item';

const TABLE = 'portal_coverage_requests';
const BASE_SELECT = '*';
const ADMIN_SELECT = '*, requester:portal_org_member!fk_pcr_requester(name)';

type RequestRow = {
  id: string;
  org_id: string;
  requested_by: string;
  requested_at: string;
  status: string;
  status_updated_at: string | null;
  items: unknown;
  pricing_snapshot: unknown;
  admin_notes: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
};

type RequestRowWithJoin = RequestRow & {
  requester: { name: string } | null;
};

@Injectable()
export class SupabaseCoverageRequestRepository extends CoverageRequestRepository {
  private readonly authStore = inject(AuthStore);

  private client(): Promise<SupabaseClient> {
    return this.authStore.getSupabaseClient();
  }

  async forOrg(orgId: OrgId): Promise<readonly CoverageRequest[]> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from(TABLE)
      .select(BASE_SELECT)
      .eq('org_id', orgId)
      .order('requested_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as RequestRow[]).map(rowToRequest);
  }

  async listAll(): Promise<CoverageRequestListResult> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from(TABLE)
      .select(ADMIN_SELECT)
      .order('requested_at', { ascending: false });
    if (error) throw error;
    const requesterNames = new Map<string, string>();
    const requests: CoverageRequest[] = [];
    for (const row of (data ?? []) as RequestRowWithJoin[]) {
      const { requester, ...rest } = row;
      requests.push(rowToRequest(rest));
      if (requester?.name) requesterNames.set(rest.requested_by, requester.name);
    }
    return { requests, requesterNames };
  }

  async submit(input: CoverageRequestSubmitInput): Promise<{ requestId: CoverageRequestId }> {
    const supabase = await this.client();
    const { data, error } = await supabase.rpc('submit_coverage_request', {
      p_org_id: input.orgId,
      p_items: input.items,
      p_pricing_snapshot: input.pricingSnapshot,
    });
    if (error) throw error;
    const result = data as { success: boolean; request_id?: string; error?: string };
    if (!result.success || !result.request_id) {
      throw new Error(result.error ?? 'submit_coverage_request returned no request_id');
    }
    return { requestId: CoverageRequestId(result.request_id) };
  }

  async resolve(
    requestId: CoverageRequestId,
    status: CoverageRequestStatus,
    notes: string | null
  ): Promise<CoverageRequestResolveResult> {
    const supabase = await this.client();
    const { data, error } = await supabase.rpc('resolve_coverage_request', {
      p_request_id: requestId,
      p_status: status,
      p_admin_notes: notes,
    });
    if (error) return { success: false, error: error.message };
    const result = data as { success: boolean; error?: string };
    return { success: result.success, error: result.error };
  }
}

function rowToRequest(row: RequestRow): CoverageRequest {
  return {
    kind: 'coverage-request',
    id: CoverageRequestId(row.id),
    orgId: OrgId(row.org_id),
    requestedBy: UserId(row.requested_by),
    requestedAt: row.requested_at,
    status: toStatus(row.status),
    statusUpdatedAt: row.status_updated_at,
    items: toItems(row),
    pricingSnapshot: parsePricingSnapshot(
      row.pricing_snapshot,
      `coverage request ${row.id} pricing snapshot`
    ),
    adminNotes: row.admin_notes,
    resolvedBy: row.resolved_by ? UserId(row.resolved_by) : null,
    resolvedAt: row.resolved_at,
  };
}

function toStatus(raw: string): CoverageRequestStatus {
  switch (raw) {
    case 'pending':
    case 'in_review':
    case 'approved':
    case 'rejected':
    case 'cancelled':
      return raw;
    default:
      return 'pending';
  }
}

function toItems(row: RequestRow): readonly OrderLineItem[] {
  if (!Array.isArray(row.items)) {
    throw new Error(`coverage request ${row.id}: items is not an array`);
  }
  return row.items.map((item, index) =>
    parseOrderLineItem(item, `coverage request ${row.id} line item ${index}`)
  );
}
